import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
import { calculateVacantPossessionValueRate } from './aggregates.calc.js';
/**
 * SA6 — Vacant Possession Value Rate, ported from `sale-auto-calc-listeners.ts`
 * `calculateVacantPossessionValueRate` (~773-841):
 *
 *   vacant_possession_value_rate = vacant_possession_value_amount / net_rentable_area(in m²)
 *
 * The NRA -> square-metre conversion happens INSIDE the calc (unlike every other sale rule),
 * mirroring the legacy `makeAmountWith` + `amountAs(Units.SquareMeter, ...)` call. The rate-UOM
 * write (CurrencyAreaMapping[amountUOM]['Square Meter'] || `${amountUOM}/SqM`, i.e. forcing a
 * '/Square Meter' rate) is host-owned (T2.7).
 *
 * Null semantics (verbatim): amount null/0 OR nra null/0 -> write null (legacy clears the value);
 * non-finite division -> write null; otherwise round via `deps.round` (legacy '0.2-2' format).
 */
/** m² per square foot in `uom-units` (Units.SquareFeet = (0.3048 m)²). */
const SQM_PER_SQFT = 0.09290304;
/**
 * m² factors keyed by the lower-cased UOM aliases from
 * `projects/miq/utils/src/lib/uom-calculator/units-definition.ts` UOMS (area entries).
 * NOTE the legacy Acre is a custom `Unit.timesNumber('Acre', 4046.86, SquareMeter)` —
 * 4046.86 exactly, NOT the international 4046.8564224 — pinned as-is for parity.
 */
const SQM_PER_AREA_ALIAS = {
  // SQUARE_FOOT aliases
  sf: SQM_PER_SQFT,
  'square foot': SQM_PER_SQFT,
  sqf: SQM_PER_SQFT,
  'square feet': SQM_PER_SQFT,
  // SQUARE_METER aliases
  sm: 1,
  'square meter': 1,
  'square meters': 1,
  sqm: 1,
  sqmt: 1,
  sqmts: 1,
  'square mt': 1,
  'sq m': 1,
  // SQUARE_KILOMETER aliases (SquaredKilometer = 1e6 m²)
  skm: 1e6,
  'square kilometers': 1e6,
  sqkm: 1e6,
  sqkms: 1e6,
  'square km': 1e6,
  'sq km': 1e6,
  // SQUARE_MILE aliases (Squared(Mile), Mile = 1609.344 m)
  sml: 2589988.110336,
  'square mile': 2589988.110336,
  'square miles': 2589988.110336,
  sqml: 2589988.110336,
  sqmile: 2589988.110336,
  'sq mi': 2589988.110336,
  // ACRE aliases (custom 4046.86 factor — see note above)
  acre: 4046.86,
  acres: 4046.86,
  acr: 4046.86,
  ac: 4046.86,
  // HECTARE aliases (10000 m²)
  hectarea: 10000,
  hec: 10000,
  hectare: 10000,
  ha: 10000
};
/**
 * Length aliases from the same UOMS table. Legacy `amountAs(SquareMeter, lengthAmount)` throws a
 * quantity-mismatch error inside the try/catch, so the raw (unconverted) value is used.
 */
const LENGTH_ALIASES = new Set(['foot', 'feet', 'ft', 'in', 'inches', 'inch', 'meter', 'mt', 'mts', 'meters', 'kilometer', 'kilometers', 'km', 'kms', 'mile', 'ml', 'milla', 'miles']);
/**
 * NRA -> m², byte-matching the legacy conversion pipeline:
 *  - falsy UOM            -> raw value (legacy `if (netRentableAreaUOM)` gate skips conversion)
 *  - known area alias     -> value × factor (case-insensitive: legacy `findUnit` lower-cases)
 *  - known length alias   -> raw value (legacy conversion throws, caught, falls back to raw)
 *  - anything else        -> value × SQM_PER_SQFT (legacy `getSafeUnit` defaults unknown UOMs to SquareFeet)
 */
export function netRentableAreaToSquareMeters(value, uom) {
  if (!uom) {
    return value;
  }
  const alias = String(uom).toLowerCase();
  const factor = SQM_PER_AREA_ALIAS[alias];
  if (factor != null) {
    return value * factor;
  }
  if (LENGTH_ALIASES.has(alias)) {
    return value;
  }
  return value * SQM_PER_SQFT;
}
/** SA6: vacant_possession_value_rate = vacant_possession_value_amount / NRA-converted-to-m². */
export const vacantPossessionValueRateRule = {
  id: 'sale.vacant-possession-value-rate.from-amount-nra',
  inputs: [FieldKey.VacantPossessionValueAmount, FieldKey.NetRentableArea, FieldKey.NetRentableAreaUom],
  outputs: [FieldKey.VacantPossessionValueRate],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const amount = ctx.get(FieldKey.VacantPossessionValueAmount);
    const nra = ctx.get(FieldKey.NetRentableArea);
    // Legacy: either value null/undefined/0 -> clear the rate (write null), before any conversion.
    if (amount == null || amount === 0 || nra == null || nra === 0) {
      ctx.set(FieldKey.VacantPossessionValueRate, null);
      return;
    }
    const nraInSqM = netRentableAreaToSquareMeters(Number(nra), ctx.get(FieldKey.NetRentableAreaUom));
    const rate = calculateVacantPossessionValueRate(Number(amount), nraInSqM);
    ctx.set(FieldKey.VacantPossessionValueRate, rate != null && Number.isFinite(rate) ? round(rate, FieldKey.VacantPossessionValueRate) : null);
  }
};
export const vpvrRules = [vacantPossessionValueRateRule];
